import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import { fetchMe, type CurrentUser } from '@/lib/auth';

const GUEST_FLAG_KEY = 'motogo.guest';
const GUEST_NAME_KEY = 'motogo.guestName';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: CurrentUser }
  | { status: 'guest'; name: string }
  | { status: 'error'; error: string };

interface AuthContextValue {
  state: AuthState;
  /** Re-fetch /me; useful after a settings update changes the cached user. */
  refresh: () => Promise<void>;
  /** Mark the session as guest. Persisted in localStorage so reloads stay in guest mode. */
  continueAsGuest: () => void;
  /** Drop guest mode and show the login page again (e.g. "Sign in to your account"). */
  exitGuestMode: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readGuestFlag(): boolean {
  try {
    return localStorage.getItem(GUEST_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeGuestFlag(value: boolean): void {
  try {
    if (value) localStorage.setItem(GUEST_FLAG_KEY, 'true');
    else localStorage.removeItem(GUEST_FLAG_KEY);
  } catch {
    // localStorage unavailable (private mode, quota) -- guest will need to re-confirm
    // on next load. Non-fatal.
  }
}

/**
 * Stable, human-distinguishable label for a guest (e.g. "Guest 4821"). Generated once and
 * persisted so it survives reloads within the same guest session, and so two guests in a
 * voice channel don't both show up as a bare "Guest".
 */
function readOrCreateGuestName(): string {
  const fresh = () => `Guest ${1000 + Math.floor(Math.random() * 9000)}`;
  try {
    const existing = localStorage.getItem(GUEST_NAME_KEY);
    if (existing) return existing;
    const name = fresh();
    localStorage.setItem(GUEST_NAME_KEY, name);
    return name;
  } catch {
    // localStorage unavailable -- fall back to an ephemeral name for this load.
    return fresh();
  }
}

function clearGuestName(): void {
  try {
    localStorage.removeItem(GUEST_NAME_KEY);
  } catch {
    // ignore
  }
}

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ status: 'loading' });
    try {
      const user = await fetchMe();
      if (user) {
        // Real session takes precedence -- clear any stale guest flag + name.
        writeGuestFlag(false);
        clearGuestName();
        setState({ status: 'authenticated', user });
        return;
      }
      // No session. If user previously chose "Continue as guest", honour it.
      if (readGuestFlag()) {
        setState({ status: 'guest', name: readOrCreateGuestName() });
        return;
      }
      setState({ status: 'unauthenticated' });
    } catch (err) {
      setState({ status: 'error', error: err instanceof Error ? err.message : 'Unknown error' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const continueAsGuest = useCallback(() => {
    writeGuestFlag(true);
    setState({ status: 'guest', name: readOrCreateGuestName() });
  }, []);

  const exitGuestMode = useCallback(() => {
    writeGuestFlag(false);
    clearGuestName();
    setState({ status: 'unauthenticated' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, refresh: load, continueAsGuest, exitGuestMode }),
    [state, load, continueAsGuest, exitGuestMode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
